import { OWNER_ONLY } from "../context/MembershipContext";

/**
 * The dashboard's screens, the permission each one needs to be offered, and
 * the plan module it belongs to, if any.
 * The sidebar lists them and the route guards check them — one list, so a
 * screen cannot be hidden from the menu yet reachable by its address.
 *
 * Where a screen shows up:
 * - `group` puts it under a heading in the drawer (none: it stands alone, on top);
 * - `hub` folds it with the screens of the same hub into one drawer entry that
 *   opens on tabs — one process, several screens;
 * - `placement: "account"` moves it out of the drawer into the avatar menu, for
 *   the set-up and audit screens that are opened far less than the rest.
 */
export const NAVIGATION = [
  { key: "dashboard", path: "/admin", icon: "📊", permission: "analytics.view" },
  { key: "products", path: "/admin/products", icon: "📦", permission: "products.view", group: "catalogue" },
  { key: "ranges", path: "/admin/ranges", icon: "🗂️", permission: "products.view", group: "catalogue" },
  { key: "packs", path: "/admin/packs", icon: "🧰", permission: "packs.view", module: "packs", group: "catalogue" },
  { key: "offers", path: "/admin/offers", icon: "🎁", permission: "offers.view", module: "offers", group: "catalogue" },
  { key: "purchases", path: "/admin/purchases", icon: "💰", permission: "purchases.view", group: "sales" },
  { key: "preorders", path: "/admin/preorders", icon: "📅", permission: "preorders.view", module: "preorders", group: "sales" },
  { key: "credit", path: "/admin/credit", icon: "🤝", permission: "credit.view", module: "credit_sales", group: "sales" },
  { key: "revenue", path: "/admin/revenue", icon: "🧮", permission: "revenue.view", module: "finance", group: "money" },
  { key: "expenses", path: "/admin/expenses", icon: "💸", permission: "expenses.view", module: "finance", group: "money" },
  { key: "cash", path: "/admin/cash", icon: "💵", permission: "power.manage_cash", module: "cash_drawer", group: "money" },
  { key: "savings", path: "/admin/savings", icon: "🏦", permission: OWNER_ONLY, module: "savings", group: "money" },
  { key: "stock", path: "/admin/stock", icon: "📉", permission: "products.view", group: "stock" },
  { key: "deliveries", path: "/admin/deliveries", icon: "🚚", permission: "deliveries.view", module: "finance", group: "stock", hub: "supply" },
  { key: "suppliers", path: "/admin/suppliers", icon: "🏭", permission: "suppliers.view", module: "suppliers", group: "stock", hub: "supply" },
  { key: "materials", path: "/admin/materials", icon: "🧪", permission: "mixing.view", module: "mixing", group: "stock", hub: "mixing" },
  { key: "recipes", path: "/admin/recipes", icon: "📖", permission: "mixing.view", module: "mixing", group: "stock", hub: "mixing" },
  { key: "batches", path: "/admin/batches", icon: "⏳", permission: "mixing.view", module: "mixing", group: "stock", hub: "mixing" },
  { key: "reminders", path: "/admin/reminders", icon: "⏰", permission: OWNER_ONLY, module: "reminders", placement: "account" },
  { key: "shop", path: "/admin/shop", icon: "🏪", permission: "shop.view", placement: "account" },
  { key: "journal", path: "/admin/journal", icon: "📜", permission: OWNER_ONLY, module: "journal", placement: "account" },
  { key: "staff", path: "/admin/staff", icon: "👥", permission: OWNER_ONLY, placement: "account" },
  { key: "devices", path: "/admin/devices", icon: "📱", permission: OWNER_ONLY, placement: "account" },
  { key: "modules", path: "/admin/modules", icon: "🧩", permission: OWNER_ONLY, flag: "modules_screen", placement: "account" },
  { key: "subscription", path: "/admin/subscription", icon: "💳", permission: OWNER_ONLY, flag: "owner_subscription", placement: "account" },
  { key: "referrals", path: "/admin/referrals", icon: "🎟️", permission: OWNER_ONLY, flag: "referrals", placement: "account" },
  { key: "settings", path: "/admin/settings", icon: "⚙️", permission: "settings.view", placement: "account" },
];

/** Headings of the drawer, in the order they appear. */
export const GROUPS = ["catalogue", "sales", "money", "stock"];

/** Drawer entries that stand for several screens, each opening on tabs. */
export const HUBS = {
  supply: { icon: "🚚" },
  mixing: { icon: "🧪" },
};

/**
 * At or under this many drawer entries a heading only gets in the way, so the
 * drawer stays a flat list — which is what a cashier with few permissions sees.
 */
export const FLAT_LIMIT = 8;

/**
 * What the drawer lists for the screens this member is offered: one entry per
 * screen, or per hub. A hub's entry opens its first offered screen, so a shop
 * with a module off still gets the tab it has.
 */
export const drawerEntries = (offered) => {
  const entries = [];
  const hubs = new Map();

  offered
    .filter((item) => item.placement !== "account")
    .forEach((item) => {
      const hub = item.hub && hubs.get(item.hub);
      if (hub) {
        hub.screens.push(item);
        return;
      }

      const entry = {
        id: item.hub || item.key,
        labelKey: item.hub ? `nav.hubs.${item.hub}` : `nav.${item.key}`,
        icon: item.hub ? HUBS[item.hub].icon : item.icon,
        path: item.path,
        group: item.group,
        screens: [item],
      };
      if (item.hub) hubs.set(item.hub, entry);
      entries.push(entry);
    });

  return entries;
};

/** The screens of the hub a path belongs to, if it belongs to one. */
export const hubTabsFor = (pathname, offered) => {
  const current = NAVIGATION.find((item) => item.hub && pathname.startsWith(item.path));
  if (!current) return [];
  return offered.filter((item) => item.hub === current.hub);
};

export const screenFor = (key) => NAVIGATION.find((item) => item.key === key);

/** Where to send someone whose screen is not theirs: the first one that is. */
export const firstAllowedPath = (isOffered) =>
  NAVIGATION.find((item) => isOffered(item))?.path || null;
